import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSeo } from '../hooks/useSeo';
import { trackLead } from '../analytics';
import { Footer } from '../components/Footer';
import { Navigation } from '../components/Navigation';
import { LEGAL_ROUTES } from './legal/legal';
import './EarlyAccessPage.css';

const API_URL = import.meta.env.VITE_EARLY_ACCESS_API_URL ?? '';

const YEARS_OPTIONS = [
  { value: '', label: 'Select…' },
  { value: 'under-1', label: 'Under 1 year' },
  { value: '1-2', label: '1–2 years' },
  { value: '3-5', label: '3–5 years' },
  { value: '6-10', label: '6–10 years' },
  { value: '10-plus', label: 'More than 10 years' },
];

export function EarlyAccessPage() {
  useSeo({
    title: 'Get Early Access — Revvly Subscription Revenue Management',
    description:
      'Join the Revvly founding cohort: early access to AI subscription revenue management, with billing, pricing, CRM and MRR/ARR analytics in one platform.',
    path: '/early-access',
    type: 'website',
  });

  const [companyName, setCompanyName] = useState('');
  const [website, setWebsite] = useState('');
  const [email, setEmail] = useState('');
  const [yearsInBusiness, setYearsInBusiness] = useState('');
  const [idealStart, setIdealStart] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setErrorMessage('');

    if (!API_URL.trim()) {
      setStatus('error');
      setErrorMessage(
        'This form is not connected yet. Set VITE_EARLY_ACCESS_API_URL to your Worker URL (e.g. http://127.0.0.1:8787/api/early-access for local dev).',
      );
      return;
    }

    if (honeypot) {
      setStatus('success');
      return;
    }

    if (!accepted) {
      setStatus('error');
      setErrorMessage(
        'Please accept the Terms of Service, Privacy Policy, and Cookie Policy to continue.',
      );
      return;
    }

    setStatus('submitting');

    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          companyName,
          website,
          email,
          yearsInBusiness,
          idealStart,
          acceptedPolicies: accepted,
          honeypot,
        }),
      });

      const data = (await res.json().catch(() => null)) as { ok?: boolean; error?: string } | null;

      if (!res.ok || !data?.ok) {
        throw new Error(data?.error ?? `Request failed (${res.status})`);
      }

      setStatus('success');
      trackLead();
      setCompanyName('');
      setWebsite('');
      setEmail('');
      setYearsInBusiness('');
      setIdealStart('');
      setAccepted(false);
    } catch (err) {
      setStatus('error');
      setErrorMessage(
        err instanceof Error ? err.message : 'Something went wrong. Please try again.',
      );
    }
  }

  return (
    <div className="early-access">
      <Navigation />

      <main className="early-access__main">
        <div className="early-access__panel">
          <p className="early-access__eyebrow">Early access</p>
          <h1 className="early-access__title">Get started with Revvly</h1>
          <p className="early-access__lead">
            Tell us a bit about your company. We will follow up by email.
          </p>

          {status === 'success' ? (
            <div className="early-access__success" role="status">
              <p className="early-access__success-title">Thanks — you are on the list.</p>
              <p className="early-access__success-text">
                We will reach out at the address you provided.
              </p>
              <Link to="/" className="early-access__back early-access__back--inline">
                Back to home
              </Link>
            </div>
          ) : (
            <form className="early-access__form" onSubmit={handleSubmit} noValidate>
              <label className="early-access__field">
                <span className="early-access__label">Company name</span>
                <input
                  className="early-access__input"
                  name="companyName"
                  type="text"
                  autoComplete="organization"
                  required
                  value={companyName}
                  onChange={(ev) => setCompanyName(ev.target.value)}
                  placeholder="Acme Inc."
                />
              </label>

              <label className="early-access__field">
                <span className="early-access__label">Website</span>
                <input
                  className="early-access__input"
                  name="website"
                  type="text"
                  inputMode="url"
                  autoComplete="url"
                  required
                  value={website}
                  onChange={(ev) => setWebsite(ev.target.value)}
                  placeholder="https://example.com"
                />
              </label>

              <label className="early-access__field">
                <span className="early-access__label">Email address</span>
                <input
                  className="early-access__input"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(ev) => setEmail(ev.target.value)}
                  placeholder="you@company.com"
                />
              </label>

              <label className="early-access__field">
                <span className="early-access__label">Years in business</span>
                <select
                  className="early-access__input early-access__select"
                  name="yearsInBusiness"
                  required
                  value={yearsInBusiness}
                  onChange={(ev) => setYearsInBusiness(ev.target.value)}
                >
                  {YEARS_OPTIONS.map((opt) => (
                    <option
                      key={opt.value || 'empty'}
                      value={opt.value}
                      disabled={opt.value === ''}
                    >
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="early-access__field">
                <span className="early-access__label">When would you like to get started?</span>
                <textarea
                  className="early-access__input early-access__textarea"
                  name="idealStart"
                  required
                  rows={3}
                  value={idealStart}
                  onChange={(ev) => setIdealStart(ev.target.value)}
                  placeholder="e.g. Next month, Q2 2026, or as soon as possible"
                />
              </label>

              <div className="early-access__hp" aria-hidden="true">
                <label>
                  Do not fill
                  <input
                    type="text"
                    name="_gotcha"
                    tabIndex={-1}
                    autoComplete="off"
                    value={honeypot}
                    onChange={(ev) => setHoneypot(ev.target.value)}
                  />
                </label>
              </div>

              {/* Links are kept OUT of the <label> — a link inside a label can
                  toggle the checkbox (or fail to open) in some browsers. The
                  checkbox carries its own accessible name via aria-label. */}
              <div className="early-access__consent">
                <input
                  id="ea-consent"
                  className="early-access__consent-input"
                  type="checkbox"
                  name="acceptedPolicies"
                  checked={accepted}
                  onChange={(ev) => setAccepted(ev.target.checked)}
                  required
                  aria-labelledby="ea-consent-text"
                />
                <span id="ea-consent-text" className="early-access__consent-text">
                  I agree to Revvly&rsquo;s{' '}
                  <Link to={LEGAL_ROUTES.terms} target="_blank" rel="noopener noreferrer">
                    Terms of Service
                  </Link>
                  ,{' '}
                  <Link to={LEGAL_ROUTES.privacy} target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </Link>
                  , and{' '}
                  <Link to={LEGAL_ROUTES.cookies} target="_blank" rel="noopener noreferrer">
                    Cookie Policy
                  </Link>
                  .
                </span>
              </div>

              {status === 'error' && errorMessage && (
                <p className="early-access__error" role="alert">
                  {errorMessage}
                </p>
              )}

              <div className="early-access__actions">
                <button
                  type="submit"
                  className="early-access__submit"
                  disabled={status === 'submitting' || !accepted}
                >
                  {status === 'submitting' ? 'Sending…' : 'Request early access'}
                </button>
              </div>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
